import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Users, Loader2, ArrowRight, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { trustApi } from "../../services/api";

interface Props {
  bankAccountId: string;
  onRefresh: () => void;
}

interface SubledgerEntry {
  _id: string;
  clientId: { _id: string; displayName: string } | null;
  matterId: { _id: string; title: string } | null;
  ledgerBalanceCents: number;
  availableBalanceCents: number;
  lastMovementAt: string | null;
}

function formatCents(cents: number): string {
  const isNegative = cents < 0;
  const abs = Math.abs(cents);
  const formatted = new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(abs / 100);
  return isNegative ? `-${formatted}` : formatted;
}

function formatDate(iso: string | null): string {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-CA", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function balanceColor(cents: number): string {
  if (cents < 0) return "text-red-600";
  if (cents === 0) return "text-slate-400";
  return "text-emerald-700";
}

function balanceBg(cents: number): string {
  if (cents < 0) return "bg-red-50";
  if (cents === 0) return "bg-slate-50";
  return "bg-emerald-50";
}

const TrustOverviewTab: React.FC<Props> = ({ bankAccountId, onRefresh }) => {
  const [entries, setEntries] = useState<SubledgerEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);

  const fetchSubledger = useCallback(async () => {
    if (!bankAccountId) {
      setEntries([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await trustApi.getSubledger({ bankAccountId, limit: 100 });
      setEntries(res.data?.data || []);
    } catch (err) {
      console.error("Failed to load subledger:", err);
    } finally {
      setLoading(false);
    }
  }, [bankAccountId]);

  useEffect(() => {
    fetchSubledger();
  }, [fetchSubledger]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!bankAccountId) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-12 text-center">
        <Users className="h-12 w-12 mx-auto mb-3 text-slate-300" />
        <p className="text-lg font-medium text-slate-600">No Trust Account Selected</p>
        <p className="text-sm text-slate-400 mt-1">
          Select a trust bank account from the dropdown above to view balances
        </p>
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-12 text-center">
        <Users className="h-12 w-12 mx-auto mb-3 text-slate-300" />
        <p className="text-lg font-medium text-slate-600">No Trust Balances</p>
        <p className="text-sm text-slate-400 mt-1">
          Create trust receipts to see client balances here
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-200">
        <h2 className="text-lg font-semibold text-slate-800">Trust Balance per Client</h2>
        <p className="text-sm text-slate-500 mt-0.5">
          {entries.length} active client{entries.length !== 1 ? "s" : ""} with trust balances
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-200" style={{ backgroundColor: "var(--color-primary)" }}>
              <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">
                Client
              </th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">
                Matter
              </th>
              <th className="text-right px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">
                Ledger Balance
              </th>
              <th className="text-right px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">
                Available Balance
              </th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider hidden lg:table-cell">
                Last Movement
              </th>
              <th className="px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider w-10"></th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry, i) => {
              const clientName =
                typeof entry.clientId === "object" && entry.clientId
                  ? entry.clientId.displayName
                  : "Unknown Client";
              const matterTitle =
                typeof entry.matterId === "object" && entry.matterId
                  ? entry.matterId.title || "Untitled"
                  : "-";

              return (
                <motion.tr
                  key={entry._id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.02 }}
                  className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() =>
                    setSelectedClientId(
                      selectedClientId === entry._id ? null : entry._id
                    )
                  }
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div
                        className="h-9 w-9 rounded-full flex items-center justify-center text-white font-semibold text-xs flex-shrink-0"
                        style={{ backgroundColor: "var(--color-accent)" }}
                      >
                        {clientName
                          .split(" ")
                          .slice(0, 2)
                          .map((w: string) => w[0]?.toUpperCase())
                          .join("")}
                      </div>
                      <span className="font-medium text-sm text-slate-800">
                        {clientName}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">{matterTitle}</td>
                  <td className="px-6 py-4 text-right">
                    <span
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${balanceBg(entry.ledgerBalanceCents)} ${balanceColor(entry.ledgerBalanceCents)}`}
                    >
                      {entry.ledgerBalanceCents > 0 && <TrendingUp className="h-3 w-3" />}
                      {entry.ledgerBalanceCents < 0 && <TrendingDown className="h-3 w-3" />}
                      {entry.ledgerBalanceCents === 0 && <Minus className="h-3 w-3" />}
                      {formatCents(entry.ledgerBalanceCents)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span className={`text-sm font-medium ${balanceColor(entry.availableBalanceCents)}`}>
                      {formatCents(entry.availableBalanceCents)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-500 hidden lg:table-cell">
                    {formatDate(entry.lastMovementAt)}
                  </td>
                  <td className="px-6 py-4">
                    <ArrowRight className="h-4 w-4 text-slate-400" />
                  </td>
                </motion.tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TrustOverviewTab;
